import React from 'react'
import { object, string } from 'yup'
import { FormikStepProps } from '../../../../components/form/wizzard/FormikStepper'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import mapEnumForSelect from '../../../../utils/mapEnumForSelect'
import {
  ChannelType,
  Iab,
  Network,
  PositionInput,
  useGetCompanyDetailsQuery,
  useWebsiteQuery,
} from '../../../../__generated__/graphql'
import { Grid } from '@mui/material'
import InputField from '../../../../components/form/InputField'
import SelectField from '../../../../components/form/SelectField'
import MenuItem from '@mui/material/MenuItem'
import { MaybeFormik } from '../../../../interfaces/maybeFormik'
import { useField } from 'formik'
import Loader from '../../../../components/loader/Loader'
import IabAutoCompleteField from '../../../../components/form/IABAutoCompleteField'

export const summarySchema = object().shape({
  name: string().min(3).required('commons.required'),
  iabCategory: string().required('commons.required'),
  channelType: string().required('commons.required'),
  network: string().required('commons.required'),
})

export interface CreatePositionSummaryInterface extends Partial<Record<keyof PositionInput, unknown>> {
  name: string
  iabCategory: Iab
  channelType: MaybeFormik<ChannelType>
  network: MaybeFormik<Network>
}

const CreatePositionDetailsStep: React.FC<
  FormikStepProps & {
    allowMissingWebsite?: boolean
    publisherCompanyId?: string | number
  }
> = ({ allowMissingWebsite = false, publisherCompanyId }) => {
  const t = useAppTranslations()

  const channelTypes = mapEnumForSelect(ChannelType)
  const networks = mapEnumForSelect(Network)
  const [websiteField] = useField('websiteId')
  const websiteId = websiteField.value
  const { data: websiteData, loading: loadingWebsite } = useWebsiteQuery({
    variables: { id: websiteId },
    skip: !websiteId,
  })
  const publisherSlug = websiteData?.website.publisher.slug
  const { data: companyFromWebsite, loading: loadingCompanyFromWebsite } = useGetCompanyDetailsQuery({
    variables: { slug: publisherSlug },
    skip: !publisherSlug,
  })
  const { data: companyFromPublisher, loading: loadingCompanyFromPublisher } = useGetCompanyDetailsQuery({
    variables: { id: publisherCompanyId },
    skip: !allowMissingWebsite || !publisherCompanyId || !!websiteId,
  })

  const companyData = websiteId ? companyFromWebsite : companyFromPublisher

  if (!websiteId && !allowMissingWebsite) return null
  if (!websiteId && allowMissingWebsite && !publisherCompanyId) return null
  if (loadingWebsite || loadingCompanyFromWebsite || loadingCompanyFromPublisher) return <Loader />

  return (
    <Grid container spacing={3}>
      <Grid size="grow">
        <InputField type={'text'} name={'name'} label={t('commons.fullName')} required variant="outlined" />
      </Grid>
      <Grid size="grow">
        <IabAutoCompleteField name={'iabCategory'} label={t('commons.iabCategory')} required />
      </Grid>
      {companyData?.company.userPermissions.web && (
        <>
          <Grid size="grow">
            <SelectField
              name={'channelType'}
              label={t('position.summary.channelType')}
              required
              variant="outlined"
              withNone={false}
            >
              {channelTypes.map((type, i) => (
                <MenuItem value={type.value} key={i}>
                  {`${t('enum.ChannelType.' + type.label)}`}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
          <Grid size="grow">
            <SelectField name={'network'} label={t('position.summary.network')} required variant="outlined">
              {networks.map((media, i) => (
                <MenuItem value={media.value} key={i}>
                  {`${t('enum.Network.' + media.label)}`}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default CreatePositionDetailsStep
